// app/src/features/timeline/renderers/renderEventBell.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import { eventGradientIdBase } from "../../../lib/forecast/eventIdentity.js";
import { bandFitsAroundPie, isLongRange } from "../../../lib/timeline/geometry.js";

export function renderEventBell(ctx, ev, x, rowId, above, spineY, circleY, r, ih, branchForkMs) {
  const { dayToScreen, effectiveToday, eventAnchorMs, themeRaw } = ctx;
  // SVG stop colors are presentation attributes that don't resolve var(), so this reads the
  // raw-hex palette.
  const C = themeRaw;
  if (ev.isPast) return null;
  // NOT suppressed for neutral triggers. This underlay is the trigger's DATE
  // distribution — when it may occur — which every trigger with a range has, whatever
  // its outcome space. It was dropped for neutral triggers on the reasoning that "a
  // distribution has no direction", but direction is not what this draws: the curve is
  // already direction-agnostic (grey stroke, `above` used only to pick which side of
  // the spine it bulges toward). Suppressing it took the WHEN away along with the
  // up/down, which under All Multiway means every trigger lost it.
  const d = r * 2;
  const gidBase = eventGradientIdBase(rowId, ev);
  // A range wide enough ON SCREEN at the current zoom gets the bell too, so a short range shows
  // its distribution when zoomed in instead of collapsing to a bare vertical connector.
  const hasRange = ev.date_end && ev.date_end !== ev.date;
  // The start is clamped to the left floor (fork for branch events, today for future events) so
  // the width reflects what actually RENDERS on screen, not the raw ev.date → ev.date_end. The
  // costly dayToScreen is deferred into showBell so a long-range event short-circuits past it.
  const rawStartMs = new Date(ev.date).getTime();
  const leftFloor = Math.max(
    branchForkMs != null ? branchForkMs : -Infinity,
    effectiveToday.getTime(),
  );
  const clampedStartMs = Math.max(rawStartMs, leftFloor);
  const endMs = hasRange ? new Date(ev.date_end).getTime() : clampedStartMs;
  const showBell =
    // A grading window is a deadline, not a spread — see `_gradingWindow`. The neutral
    // band already refuses it; the VALENCED bell must too, or a typed branch draws a range
    // curve over click..close whenever All Multiway is off.
    !ev._gradingWindow &&
    (isLongRange(ev) ||
      (hasRange &&
        endMs > clampedStartMs &&
        Math.abs(dayToScreen(new Date(endMs)) - dayToScreen(new Date(clampedStartMs))) >= 40));
  const stops = (
    <>
      <stop offset="0%" stopColor={C.gray} stopOpacity="0" />
      <stop offset="22%" stopColor={C.gray} stopOpacity="0.85" />
      <stop offset="78%" stopColor={C.gray} stopOpacity="0.85" />
      <stop offset="100%" stopColor={C.gray} stopOpacity="0" />
    </>
  );
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: 0,
        // The valenced bell is an UNDERLAY and stays under the spine (3). The neutral
        // band replaces the line across its span, so it has to sit ABOVE it — at 1 the
        // dashes showed straight through and it read as tinted line rather than a band.
        // Still below the trigger circles (10/20) and the resolution marker (30).
        zIndex: ev._neutral ? 4 : 1,
        // HOVER-ONLY, deliberately. An experiment drew every bell at rest (0.33) with the
        // lane's lead at full, on the theory that the lane's shape should be visible
        // without hunting. On a real lane it read as haze: several overlapping date
        // distributions stack around the spine and compete with the line itself, and a
        // permanently-lit lead implies a ranking the user did not ask for. So a bell is
        // the answer to "when might THIS trigger fire", shown when you ask for it.
        opacity: ih ? 1 : 0,
        transition: "opacity 1.5s ease",
        pointerEvents: "none",
      }}
    >
      {/* NEUTRAL triggers sit ON the spine, so the distribution is drawn ALONG the line
           rather than as a curve off it. A bell's peak is the circle, and for an on-spine
           circle that peak lands on the line itself, so the curve would bulge off a line it is
           supposed to annotate and compete with the line and the labels alternating on both
           sides of it. Along the line there is nothing to compete with, and the reading is the
           same: WHERE in this window the trigger is likely.

           HASHES MARK HARD BOUNDS. They are drawn only for a trigger with an explicit date
           range — a real start and end. A spread-only trigger (std_day, no date_end) has no
           such edge to mark, so it fades out instead of ending in a tick that would imply a
           bound the data does not have. */}
      {ev._neutral
        ? (() => {
            if (endMs <= clampedStartMs) return null;
            // No band over a grading window: it would read as "may happen anywhere in
            // here", the opposite of the asserted moment the user placed.
            if (ev._gradingWindow) return null;
            const xL = dayToScreen(new Date(clampedStartMs)) - x;
            const xR = dayToScreen(new Date(endMs)) - x;
            const span = xR - xL;
            // Suppressing the band leaves the circle close to where it started: the
            // midpoint anchor displaces it by span/2, and a dropped band means
            // span < 2r + 2·MIN_WING, so the offset stays within MIN_WING of the radius.
            // The circle therefore sits over its start date give or take a few px — not
            // exactly on it, so don't read this as a guarantee, only as the reason a
            // bandless trigger doesn't visibly drift.
            if (!bandFitsAroundPie(span, r)) return null;
            const meanMs = eventAnchorMs(ev, rowId);
            // The peak sits where the circle does, not at the midpoint of the dates. The
            // circle is OFF-CENTRE whenever the mode is — a skewed `mean_date`, or a window
            // whose left half is in the past and clipped to today — and that says WHEN within
            // the window, which a centred blob could not.
            const meanAt = Math.max(
              0.04,
              Math.min(0.96, (dayToScreen(new Date(meanMs)) - x - xL) / span),
            );
            // The band peaks wherever the CIRCLE is, always — there is no separate "mode"
            // case. The server sends `date`/`date_end` but no median, so the circle falls
            // back to the window's midpoint, or to wherever the label-packing pass slid it.
            // Peaking there asserts nothing extra: band and circle agree about a single
            // trigger. What the band claims is its EXTENT — "somewhere in this window" —
            // which is what we actually know; read the bulge as where the marker is, not as
            // a peak in the data.
            const gid = "span-" + gidBase;
            // BAND_H is tall enough to cover the dash line and read as a band in its own right,
            // with the fade legible along it rather than squeezed into a hairline.
            const BAND_H = 10;
            const HASH_H = 16;
            // Half-height at a point, as a fraction of BAND_H/2. The profile decays
            // CONTINUOUSLY across the whole span: a raw gaussian reaches the floor well inside
            // the window whenever std is small against it (fat in the middle, then an abrupt
            // thin spike), and a linear ramp puts a visible corner where the plateau ends. So
            // it is ONE profile for every trigger — a raised cosine anchored at the circle,
            // normalised separately on each side so it reaches the floor exactly at both
            // hashes.
            const FLOOR = 0.14;
            const weightAt = (t) => {
              const side = t < meanAt ? meanAt : 1 - meanAt;
              const d = Math.min(1, Math.abs(t - meanAt) / Math.max(side, 1e-6));
              return FLOOR + (1 - FLOOR) * 0.5 * (1 + Math.cos(Math.PI * d));
            };
            const SAMPLES = 48;
            const top = [];
            const bottom = [];
            for (let i = 0; i <= SAMPLES; i++) {
              const t = i / SAMPLES;
              const px = xL + span * t;
              const h = (BAND_H / 2) * weightAt(t);
              top.push(`${px.toFixed(2)},${(spineY - h).toFixed(2)}`);
              bottom.push(`${px.toFixed(2)},${(spineY + h).toFixed(2)}`);
            }
            const ribbonPath = `M${top.join(" L")} L${bottom.reverse().join(" L")} Z`;
            const hasHardBounds = hasRange;
            return (
              <svg
                width="1"
                height="1"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  overflow: "visible",
                  pointerEvents: "none",
                  zIndex: 5,
                }}
              >
                <defs>
                  <linearGradient
                    id={gid}
                    x1={xL}
                    y1="0"
                    x2={xR}
                    y2="0"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%" stopColor={C.gray} stopOpacity="0" />
                    {/* Opaque at the peak, so the band genuinely covers the line there. */}
                    <stop offset={`${meanAt * 100}%`} stopColor={C.white} stopOpacity="1" />
                    <stop offset="100%" stopColor={C.gray} stopOpacity="0" />
                  </linearGradient>
                </defs>
                {/* A filled closed path, not a stroke, so the gradient fill applies across
                     the whole body. */}
                <path d={ribbonPath} fill={`url(#${gid})`} />
                {hasHardBounds &&
                  [xL, xR].map((hx, hi) => (
                    <line
                      key={hi}
                      x1={hx}
                      y1={spineY - HASH_H / 2}
                      x2={hx}
                      y2={spineY + HASH_H / 2}
                      stroke={C.gray}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  ))}
              </svg>
            );
          })()
        : showBell
          ? (() => {
              // The left edge and the peak (eventAnchorMs) use the same fork/today floor, so they
              // agree.
              if (endMs <= clampedStartMs) return null;
              const meanMs = eventAnchorMs(ev, rowId); // peak = visible circle position
              const stdMs = Math.max(
                86400000,
                ev.std_day ? ev.std_day * 86400000 : (endMs - rawStartMs) / 4,
              );
              const SPINE_THICK = 8;
              const peakY = circleY + r;
              // Where the curve's tails sit. A valenced trigger's circle is off in its
              // channel, so the tails run back to the spine. A NEUTRAL one is centred ON
              // the spine (peakY === spineY), so tails at the spine would flatten the curve
              // to a straight line — they drop to the same side the label took instead, and
              // the bell reads as rising out of the line to meet its circle.
              const edgeY = ev._neutral
                ? spineY + (above ? -1 : 1) * SPINE_THICK * 3
                : above
                  ? spineY - SPINE_THICK
                  : spineY + SPINE_THICK * 2;
              const samples = 64;
              const pts = [];
              for (let i = 0; i <= samples; i++) {
                const t = i / samples;
                const dayMs = clampedStartMs + (endMs - clampedStartMs) * t;
                const z = (dayMs - meanMs) / stdMs;
                const gauss = Math.exp(-(z * z) / 2);
                pts.push([dayToScreen(new Date(dayMs)) - x, edgeY + (peakY - edgeY) * gauss]);
              }
              let pathD = `M${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
              for (let i = 1; i < pts.length - 1; i++) {
                const [x0, y0] = pts[i];
                const [x1, y1] = pts[i + 1];
                const xm = (x0 + x1) / 2,
                  ym = (y0 + y1) / 2;
                pathD += ` Q${x0.toFixed(2)},${y0.toFixed(2)} ${xm.toFixed(2)},${ym.toFixed(2)}`;
              }
              pathD += ` T${pts[pts.length - 1][0].toFixed(2)},${pts[pts.length - 1][1].toFixed(2)}`;
              const gid = "bell-" + gidBase;
              const xL = pts[0][0],
                xR = pts[pts.length - 1][0];
              return (
                <svg
                  width="1"
                  height="1"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    overflow: "visible",
                    pointerEvents: "none",
                    zIndex: 5,
                  }}
                >
                  <defs>
                    <linearGradient
                      id={gid}
                      x1={xL}
                      y1="0"
                      x2={xR}
                      y2="0"
                      gradientUnits="userSpaceOnUse"
                    >
                      {stops}
                    </linearGradient>
                  </defs>
                  <path
                    d={pathD}
                    fill="none"
                    stroke={`url(#${gid})`}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              );
            })()
          : (() => {
              // The connector deliberately shares the bell's stroke style, so a point reads as a
              // degenerate distribution.
              const top = above ? circleY + d : spineY;
              const bottom = above ? spineY : circleY;
              const gid = "conn-" + gidBase;
              return (
                <svg
                  width="1"
                  height="1"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    overflow: "visible",
                    pointerEvents: "none",
                    zIndex: 5,
                  }}
                >
                  <defs>
                    <linearGradient
                      id={gid}
                      x1="0"
                      y1={top}
                      x2="0"
                      y2={bottom}
                      gradientUnits="userSpaceOnUse"
                    >
                      {stops}
                    </linearGradient>
                  </defs>
                  <line
                    x1="0"
                    y1={top}
                    x2="0"
                    y2={bottom}
                    stroke={`url(#${gid})`}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              );
            })()}
    </div>
  );
}
